import { withMicrositeProps } from '@dustin-web/microsite-nextjs/src/with-server-data/with-microsite-props'
import { withMicrositeWrapper } from '@dustin-web/microsite-nextjs/src/with-server-data/with-microsite-wrapper'
import { InferGetServerSidePropsType } from 'next'
import { getSortBy } from '@dustin-web/product-components/src/product-list'
import {
  GetCategoryForFilterPageQuery,
  GetCategoryForFilterPageQueryVariables,
} from '@productsite/src/graphql/generated/graphql-types'
import { GetCategoryForFilterPage } from '@productsite/src/graphql/get-category-for-filter-page.graphql'
import {
  FacetParameterInput,
  parseParams,
} from '@dustin-web/microsite-components/src/utils/querystring'
import { constants } from '@dustin-web/microsite-config'
import { buildQueryState } from '@dustin-web/microsite-components/src/utils/querystate'
import FilterPageLayout, { type FilterPageMetaData, FilterPageType } from '@productsite/src/components/filters/filter-page-layout'
import { getCategoryFromPath, normalizePath } from '@productsite/src/lib/get-category-from-path'
import { getInfiniteCacheKeyFromDocument } from '@dustin-web/microsite-data/src/hooks/use-graphql-infinite'
import { infinityScrollPageSize } from '@productsite/src/components/constants'
import { useI18n } from '@dustin-web/microsite-components/src/i18n'
import { uniqueArray } from '@productsite/src/components/utils/unique-array'

type Props = InferGetServerSidePropsType<typeof getServerSideProps>

const CampaignProductsPage = ({ categoryId }: Props) => {
  const { translate } = useI18n()

  const metaData: FilterPageMetaData = {
    type: FilterPageType.Campaign,
    urlPartial: translate('Campaigns_URL'),
    description: translate('CampaignMetaDescription'),
    seoText: translate('CampaignDescription'),
    title: translate('Campaign_Products'),
    facetsFilterExpression: ('campaign_b2b' || 'campaign_b2c'),
    digitalDataOrigin: 'CampaignPage',
    showOnlyUrlQuery: 'campaign'
  }

  return (
    <FilterPageLayout
      categoryId={categoryId}
      pageMetaData={metaData}
      predefinedFacets={campaignOnlyFacets}
    />
  )
}

export const getServerSideProps = withMicrositeProps(async ctx => {
  const { query, micrositeProps } = ctx
  const { cookies, fetchGraphql, requestContext, isToggleEnabled } = micrositeProps
  const { [constants.cookies.userSettings.sortBy]: sortBy } = cookies
  const parsedParams = parseParams(query)
  const queryState = buildQueryState(parsedParams)
  const { activeFilters: facets, page, searchwithin } = queryState
  const { log } = await import('@dustin-web/microsite-config/src/logger')

  const slugPath = Array.isArray(query.path) ? normalizePath(query.path.join('/')) : ''

  const props = {
    categoryId: HARDWARE_CATEGORY_ID,
    fallback: {},
  }

  if (slugPath) {
    const category = await getCategoryFromPath(
      slugPath,
      requestContext,
      isToggleEnabled('use-request-logging'),
      cookies
    )

    if (category) {
      props.categoryId = category.categoryId
    }
  }

  try {
    const combinedFacets = facets.concat(campaignOnlyFacets)

    const variables = {
      categoryId: props.categoryId,
      facets: uniqueArray(combinedFacets),
      page: page,
      pageSize: infinityScrollPageSize,
      searchPhrase: searchwithin,
      sortBy: getSortBy(sortBy),
    }

    const [_filterKey, filterData] = await fetchGraphql<
      GetCategoryForFilterPageQuery,
      GetCategoryForFilterPageQueryVariables
    >(GetCategoryForFilterPage, {
      variables,
    })

    props.fallback = {
      [getInfiniteCacheKeyFromDocument(GetCategoryForFilterPage, variables)]: [filterData],
    }

    return { props }
  } catch (err) {
    log.error(
      { err, traceIdentifier: requestContext.correlationId },
      'Error when loading data on campaign products page'
    )
    props.fallback = {}

    return { notFound: true, props }
  }
})

export const campaignOnlyFacets = [
  { name: 'showonly', values: ['campaign'] },
  { name: 'campaign', values: ['true'] },
] as FacetParameterInput[]

const HARDWARE_CATEGORY_ID = '2037738'

export default withMicrositeWrapper(CampaignProductsPage)
